// Sound Effects Utility
// Manages playing sound effects for key user actions

class SoundManager {
  constructor() {
    this.sounds = {
      login: null,
      testComplete: null,
      gameComplete: null,
      notification: null,
      success: null
    };
    this.enabled = this.getSoundPreference();
    this.volume = 0.5;
  }

  // Get user's sound preference from localStorage
  getSoundPreference() {
    const preference = localStorage.getItem('soundEnabled');
    return preference === null ? true : preference === 'true';
  }

  // Set user's sound preference
  setSoundPreference(enabled) {
    this.enabled = enabled;
    localStorage.setItem('soundEnabled', enabled.toString());
  }

  // Initialize sounds with fallback to online sources
  initializeSounds() {
    // Using free sound effects from various sources
    // These are lightweight and royalty-free
    
    this.sounds.login = this.createAudio('https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3');
    this.sounds.testComplete = this.createAudio('https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3');
    this.sounds.gameComplete = this.createAudio('https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3');
    this.sounds.notification = this.createAudio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
    this.sounds.success = this.createAudio('https://assets.mixkit.co/active_storage/sfx/1339/1339-preview.mp3');
  }

  createAudio(src) {
    try {
      const audio = new Audio(src);
      audio.volume = this.volume;
      audio.preload = 'auto';
      return audio;
    } catch (error) {
      console.error('Error creating audio:', error);
      return null;
    }
  }

  // Play a specific sound
  play(soundName) {
    if (!this.enabled) return;
    
    const sound = this.sounds[soundName];
    if (!sound) {
      console.warn(`Sound "${soundName}" not found`);
      return;
    }

    try {
      // Clone and play to allow multiple simultaneous sounds
      const audioClone = sound.cloneNode();
      audioClone.volume = this.volume;
      audioClone.play().catch(err => {
        console.warn('Audio playback failed:', err);
      });
    } catch (error) {
      console.warn('Error playing sound:', error);
    }
  }

  // Specific sound methods
  playLogin() {
    this.play('login');
  }

  playTestComplete() {
    this.play('testComplete');
  }

  playGameComplete() {
    this.play('gameComplete');
  }

  playNotification() {
    this.play('notification');
  }

  playSuccess() {
    this.play('success');
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    Object.values(this.sounds).forEach(sound => {
      if (sound) sound.volume = this.volume;
    });
  }

  // Toggle sound on/off
  toggle() {
    this.enabled = !this.enabled;
    this.setSoundPreference(this.enabled);
    return this.enabled;
  }

  // Check if sounds are enabled
  isEnabled() {
    return this.enabled;
  }
}

// Create singleton instance
const soundManager = new SoundManager();

// Initialize sounds when DOM is ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      soundManager.initializeSounds();
    });
  } else {
    soundManager.initializeSounds();
  }
}

export default soundManager;
